import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMarketplacePaths } from '../../utils/guestAccess';
import ProgressiveImage from '../common/ProgressiveImage';
import TeslaButton from '../common/TeslaButton';
import { Stylist } from '../../types/stylist';
import { publicStylistRef } from '../../utils/publicRefs';
import stylistService from '../../services/stylistService';
import './StylistCard.scss';

interface StylistCardProps {
  stylist: Stylist;
  onClick?: () => void;
  className?: string;
  onFavoriteToggle: (e: any) => Promise<void>;
  isNearby: boolean;
  distanceLabel: string | null;
  smartScore?: number;
  isFavorite: boolean;
}

const StylistCard: React.FC<StylistCardProps> = ({ 
  stylist, 
  onClick, 
  className = '',
  distanceLabel,
  smartScore,
  isFavorite: initialIsFavorite,
  onFavoriteToggle
}) => {
  const paths = useMarketplacePaths();
  const [isFavorite, setIsFavorite] = useState(initialIsFavorite || false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Sync with prop changes
  React.useEffect(() => {
    setIsFavorite(initialIsFavorite || false);
  }, [initialIsFavorite]);
  
  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      if (onFavoriteToggle) {
        await onFavoriteToggle(e);
      } else {
        // Fallback to direct service call if no handler provided
        if (isFavorite) {
          await stylistService.removeFavorite(stylist._id);
        } else {
          await stylistService.addFavorite(stylist._id);
        }
        setIsFavorite(!isFavorite);
      }
    } catch (error) {
      // Silently log error - UI state remains unchanged on failure
      console.error('[StylistCard] Error toggling favorite:', error);
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.stylist-card-modern__favorite')) {
      return;
    }
    if (onClick) {
      onClick();
    }
  };

  const rating = (stylist as any).metrics?.ratings?.average || stylist.ratings?.average || stylist.rating || 0;
  const reviewCount = (stylist as any).metrics?.ratings?.count || stylist.ratings?.count || stylist.reviewCount || 0;
  const specialties = stylist.specialties || [];
  
  const stylistWithExtras = stylist as any;
  const categories = stylistWithExtras.stylistProfile?.categories || [];
  const allSpecialties = specialties.length > 0 ? specialties : categories;
  
  // Generate initials
  const getInitials = (name: string) => {
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 0) return 'S';
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };
  
  // Extract profile photo URL
  const getProfilePhotoUrl = (): string | null => {
    if (!stylist.profilePhoto) return null;
    const photo = stylist.profilePhoto as any;
    if (typeof photo === 'object' && photo !== null) {
      if (photo.url && typeof photo.url === 'string' && photo.url.trim() !== '') {
        return photo.url;
      }
      return null;
    }
    if (typeof photo === 'string' && photo.trim() !== '') {
      return photo;
    }
    return null;
  };
  
  const profilePhotoUrl = getProfilePhotoUrl();
  
  React.useEffect(() => {
    setImageError(false);
    setImageLoaded(false);
  }, [profilePhotoUrl]);
  
  const hasProfilePhoto = !imageError && profilePhotoUrl && profilePhotoUrl.trim() !== '';
  
  const handleImageError = () => {
    if (!imageError) {
      setImageError(true);
      setImageLoaded(false);
    }
  };

  // Calculate price range
  const getPriceRange = () => {
    const services = stylistWithExtras.stylistServices;
    if (services && Array.isArray(services) && services.length > 0) {
      const prices = services
        .map((service: any) => {
          const price = service.service?.basePrice?.min || 
                       service.service?.finalPrice || 
                       service.service?.originalPrice || 
                       service.customization?.price?.base || 
                       service.service?.basePrice || 0;
          return typeof price === 'number' ? price : 0;
        })
        .filter((p: number) => p > 0);
      
      if (prices.length > 0) {
        const min = Math.min(...prices);
        return `From $${min}`;
      }
    }
    return null;
  };

  const priceRange = getPriceRange();
  
  return (
    <Link 
      to={paths.stylist(publicStylistRef(stylist))} 
      onClick={(e) => handleCardClick(e)} 
      className={`stylist-card-modern ${className}`}
    >
      <div className="stylist-card-modern__container">
        {/* Image Section */}
        <div className="stylist-card-modern__image-wrapper">
          <div className={`stylist-card-modern__image ${imageLoaded ? 'loaded' : ''}`}>
            {hasProfilePhoto ? (
              <ProgressiveImage
                key={profilePhotoUrl || 'default'}
                src={profilePhotoUrl!}
                placeholderSrc={profilePhotoUrl!}
                alt={stylist.name}
                aspectRatio="1:1"
                onLoad={() => setImageLoaded(true)}
                onError={handleImageError}
              />
            ) : (
              <div className="stylist-card-modern__initials-avatar">
                <div className="stylist-card-modern__avatar-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </div>
                <span className="stylist-card-modern__initials-text">
                  {getInitials(stylist.name || 'Stylist')}
                </span>
              </div>
            )}
            
            {/* Mobile Badge */}
            {stylist.providesMobileService && (
              <div className="stylist-card-modern__badge stylist-card-modern__badge--mobile">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99z"/>
                </svg>
                Mobile
              </div>
            )}
          </div>
        </div>

        {/* Content Section */}
        <div className="stylist-card-modern__content">
          {/* Header with Name and Favorite */}
          <div className="stylist-card-modern__header">
            <div className="stylist-card-modern__name-wrapper">
              <h3 className="stylist-card-modern__name">
                {stylist.name?.trim() || 'Stylist'}
              </h3>
              {(stylist.location?.city || stylist.location?.state) && (
                <div className="stylist-card-modern__location">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                  <span>
                    {stylist.location.city || ''}
                    {stylist.location.city && stylist.location.state ? ', ' : ''}
                    {stylist.location.state || ''}
                  </span>
                  {distanceLabel && (
                    <span className="stylist-card-modern__distance">• {distanceLabel}</span>
                  )}
                </div>
              )}
            </div>
            
            {/* Favorite Button */}
            <button 
              className={`stylist-card-modern__favorite ${isFavorite ? 'active' : ''}`}
              onClick={handleFavoriteClick}
              aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill={isFavorite ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </button>
          </div>

          {/* Rating Row */}
          <div className="stylist-card-modern__rating-row">
            {rating > 0 ? (
              <>
                <div className="stylist-card-modern__rating-badge">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  <span>{rating.toFixed(1)}</span>
                </div>
                <span className="stylist-card-modern__review-count">
                  {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
                </span>
              </>
            ) : (
              <span className="stylist-card-modern__no-reviews">New stylist</span>
            )}
            
            {/* Smart Score */}
            {typeof smartScore === 'number' && smartScore > 0 && (
              <div className="stylist-card-modern__smart-score">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2z"/>
                </svg>
                <span>{smartScore}</span>
              </div>
            )}
            
            {/* Price */}
            {priceRange && (
              <span className="stylist-card-modern__price">{priceRange}</span>
            )}
          </div>

          {/* Specialties */}
          {allSpecialties.length > 0 && (
            <div className="stylist-card-modern__specialties">
              {allSpecialties.slice(0, 2).map((category: string, index: number) => {
                const formattedCategory = typeof category === 'string' 
                  ? category.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
                  : String(category);
                return (
                  <span key={`${category}-${index}`} className="stylist-card-modern__tag">
                    {formattedCategory}
                  </span>
                );
              })}
              {allSpecialties.length > 2 && (
                <span className="stylist-card-modern__tag stylist-card-modern__tag--more">
                  +{allSpecialties.length - 2}
                </span>
              )}
            </div>
          )}

          {/* Action Button */}
          <div className="stylist-card-modern__action">
            <TeslaButton variant="primary" size="sm">
              View Profile
            </TeslaButton>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default StylistCard;
